import * as React from 'react';
import ReactDOM from 'react-dom';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import Select from 'react-select';
import DurationRange from './reporting_utils/DurationRange';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Common } from '../utils/common';

/**
 * @typedef {{
 *   group_names: string[];
 *   location_names: string[];
 *   period_end: string;
 *   period_start: '::YESTERDAY::' | '::LAST_WEEK_START::' | '::MONTH_START::' | '::LAST_MONTH_START::';
 *   report_type: string;
 *   title: string;
 *   user_id: number;
 *   user_report_id: number;
 *   user_report_parameters: {
 *     parameter_name: string;
 *     parameter_value: string;
 *     user_report_parameter_id: number;
 *   }[];
 *   user_report_recipients: {
 *     email_address: string;
 *     user_report_recipient_id: number;
 *   }[];
 *   user_report_schedules: { timezone: string; }[];
 * }} ScheduledReportData
 */

export class CustomFilters extends HTMLElement {
  constructor() {
    super();

    /** @type {ScheduledReportData | null} */
    this._data = null;
  }

  get data() {
    return this._data;
  }

  set data(data) {
    this._data = data;
    this._render();
  }

  connectedCallback() {
    this._render();
  }

  closeRow() {
    const td = /** @type {HTMLTableCellElement} */ (
      this.closest('table')?.querySelector('tr.shown td.details-control')
    );
    td.click();
  }

  _render() {
    const reportType = this.getAttribute('reporttype');

    if (!this.isConnected || !reportType || !this._data) return;

    // @ts-ignore
    ReactDOM.render(
      <CustomFiltersReact
        scheduledReportData={this._data}
        reportType={reportType}
        onCancel={this.closeRow.bind(this)}
      />,
      this
    );
  }
}

customElements.define('custom-filters', CustomFilters);

const FILTER_CONFIG = {
  tag_ids: { label: 'Tags', defaultValue: [] },
  employees: { label: 'Employees', defaultValue: [] },
  medium_type: { label: 'Referral', defaultValue: [] },
  dial_status: { label: 'Call Result', defaultValue: [] },
  ds_call_status: { label: 'Connected Type', defaultValue: [] },
  ds_department: { label: 'Department', defaultValue: [] },
  ls_call_type: { label: 'Call Types', defaultValue: [] },
  ds_appt_set: { label: 'Only by appointments', defaultValue: false },
  ds_alert_sent: { label: 'Only with alerts', defaultValue: false },
  duration: { label: 'Call Duration', defaultValue: [0, 3600] },
};

/** @type {Record<string, (keyof typeof FILTER_CONFIG)[]>} */
const reportFilterMap = {
  traffic_analysis: ['tag_ids', 'employees'],
  prospect_report: ['tag_ids'],
  acquisition: [],
  acquisition_detail: ['medium_type'],
  source_medium: [],
  inventory_overview: [],
  call_details: [
    'medium_type',
    'dial_status',
    'ds_department',
    'ds_call_status',
    'ls_call_type',
    'tag_ids',
    'duration',
    'ds_appt_set',
    'ds_alert_sent',
  ],
  lead_details: ['medium_type'],
  text_details: ['medium_type', 'tag_ids'],
  chat_details: ['medium_type'],
  vendor_ds_report: [],
  vendor_ls_report: [],
  performance_summary: [],
  numbers_and_features: [],
  active_campaigns: [],
};

/**
 * @typedef {{
 *   report_name: string;
 *   timezone: string;
 *   timeframe: string;
 *   recipients: {email: string; id: number | null;}[];
 * }} ReportDetails
 */

/**
 * @param {ScheduledReportData} scheduledReportData
 * @returns {ReportDetails}
 */
function parseReportDetails(scheduledReportData) {
  const timeframeMap = {
    '::YESTERDAY::': 'DAILY',
    '::LAST_WEEK_START::': 'PRIOR_WEEK',
    '::MONTH_START::': 'MONTH_TO_DATE',
    '::LAST_MONTH_START::': 'PRIOR_MONTH',
  };

  return {
    report_name: scheduledReportData.title,
    timezone: scheduledReportData.user_report_schedules[0].timezone,
    timeframe: timeframeMap[scheduledReportData.period_start],
    recipients: scheduledReportData.user_report_recipients.map(
      ({ email_address, user_report_recipient_id }) => ({
        email: email_address,
        id: user_report_recipient_id,
      })
    ),
  };
}

/** @typedef {{location_ids: number[], group_ids: number[]}} Domains */

/**
 * @typedef {Object} Filters
 * @property {Domains} domains
 * @property {boolean} show_inactive
 * @property {number[]} [tag_ids]
 * @property {number[]} [employees]
 * @property {string[]} [medium_type]
 * @property {string[]} [dial_status]
 * @property {string[]} [ds_call_status]
 * @property {string[]} [ds_department]
 * @property {string[]} [ls_call_type]
 * @property {number[]} [duration]
 * @property {boolean} [ds_appt_set]
 * @property {boolean} [ds_alert_sent]
 */

/**
 * @param {ScheduledReportData} scheduledReportData
 */
function parseReportParameters(scheduledReportData) {
  return scheduledReportData.user_report_parameters.reduce(
    (acc, { parameter_name, parameter_value }) => {
      switch (parameter_name) {
        case 'location_ids':
          acc.domains.location_ids = JSON.parse(parameter_value);
          break;
        case 'group_ids':
          acc.domains.group_ids = JSON.parse(parameter_value);
          break;
        case 'show_inactive': {
          const value = JSON.parse(parameter_value);
          acc.show_inactive = value == 1 ? true : false;
          break;
        }
        case 'ds_appt_set': {
          const value = JSON.parse(parameter_value);
          acc.ds_appt_set = value.length === 0 ? false : true;
          break;
        }
        case 'ds_alert_sent': {
          const value = JSON.parse(parameter_value);
          acc.ds_alert_sent = value.length === 0 ? false : true;
          break;
        }
        case 'employees': {
          const employeeIds = JSON.parse(parameter_value);
          acc.employees = employeeIds.map((id) => Number(id));
          break;
        }
        default:
          acc[parameter_name] = JSON.parse(parameter_value);
          break;
      }
      return acc;
    },
    /** @type {Filters} */ ({ domains: { location_ids: [], group_ids: [] }, show_inactive: false })
  );
}

/**
 * Returns the default filters for a given report type.
 * @param {string} reportType
 * @returns {Filters}
 */
function getInitialFiltersShape(reportType) {
  const baseFilters = {
    domains: { location_ids: [], group_ids: [] },
    show_inactive: false,
  };

  const filterKeys = reportFilterMap[reportType] || {};

  const additionalFilters = filterKeys.reduce((acc, key) => {
    if (FILTER_CONFIG[key]) {
      acc[key] = FILTER_CONFIG[key].defaultValue;
    }
    return acc;
  }, {});

  return {
    ...baseFilters,
    ...additionalFilters,
  };
}

/** @typedef {ReportDetails & Filters} FormData */

/**
 * @param {{
 *   scheduledReportData: ScheduledReportData;
 *   reportType: string;
 *   onCancel: () => void;
 * }} props
 */
function CustomFiltersReact({ scheduledReportData, reportType, onCancel }) {
  /** @type {FormData} */
  const defaultValues = React.useMemo(() => {
    const reportDetails = parseReportDetails(scheduledReportData);
    const shape = getInitialFiltersShape(reportType);
    const parsed = parseReportParameters(scheduledReportData);
    return { ...reportDetails, ...shape, ...parsed };
  }, [reportType, scheduledReportData]);

  const {
    register,
    watch,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({ defaultValues });

  const showInactive = watch('show_inactive');
  const domains = watch('domains');

  const { fields, append, remove } = useFieldArray({ control, name: 'recipients' });

  function addRecipient() {
    if (fields.length < 5) {
      append({ email: '', id: null });
    }
  }

  /**
   * @param {number} index
   */
  function removeRecipient(index) {
    if (fields.length > 1) {
      remove(index);
    }
  }

  /**
   * @param {FormData} data
   */
  async function saveChanges(data) {
    const { report_name, timezone, timeframe, recipients, domains, ...filters } = data;

    const reportDetails = {
      timezone,
      timeframe,
      name: report_name,
      emails: recipients,
    };

    const custom_filters = {
      group_ids: domains.group_ids,
      location_ids: domains.location_ids,
    };

    for (const [key, value] of Object.entries(filters)) {
      if (key === 'branding_group_id') continue;

      switch (key) {
        case 'show_inactive':
          custom_filters.show_inactive = value ? '1' : '0';
          break;
        case 'ds_appt_set':
          custom_filters.ds_appt_set = value ? ['Appointments'] : [];
          break;
        case 'ds_alert_sent':
          custom_filters.ds_alert_sent = value ? ['Alert'] : [];
          break;
        default:
          custom_filters[key] = value;
      }
    }

    const body = {
      ...reportDetails,
      custom_filters,
    };

    try {
      const res = await fetch(
        `/user_report_schedule/${scheduledReportData.user_report_id}/update_report`,
        {
          //@ts-expect-error
          headers: {
            'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
            'Content-Type': 'application/json',
          },
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );

      if (res.ok) {
        Common.notify('Scheduled report updated', 'success');
        //@ts-expect-error
        $('#scheduled_reports').DataTable().ajax.reload();
      } else {
        throw new Error('Failed to save scheduled report');
      }
    } catch (error) {
      Common.notify('Unable to update', 'danger');
    }
  }

  const availableFilters = reportFilterMap[reportType];

  return (
    <>
      <div className={`col-xs-12 form-group ${errors.domains ? 'has-error' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ marginBottom: 0 }} className='control-label'>
            Groups and Locations
          </label>
          <input
            id='show_inactive'
            style={{ margin: '0px 4px 0px 16px' }}
            type='checkbox'
            {...register('show_inactive')}
          />
          <label htmlFor='show_inactive' style={{ marginBottom: 0 }}>
            Show inactive groups and locations
          </label>
        </div>
        <Controller
          control={control}
          name='domains'
          rules={{
            validate: ({ group_ids, location_ids }) =>
              group_ids.length > 0 || location_ids.length > 0,
          }}
          render={({ field }) => (
            <DomainSelect
              initialValue={defaultValues.domains}
              onChange={field.onChange}
              showInactive={showInactive}
            />
          )}
        />
        {errors.domains && <p className='help-block'>Must select a group or location</p>}
      </div>

      <div className={`form-group col-sm-6 ${errors.report_name ? 'has-error' : ''}`}>
        <label htmlFor='report_name' className='control-label'>
          Report Title
        </label>
        <input
          {...register('report_name', { required: true, maxLength: 50 })}
          type='text'
          id='report_name'
          className='form-control'
        />
        {errors.report_name?.type === 'required' ? (
          <span className='help-block'>Must not be blank</span>
        ) : errors.report_name?.type === 'maxLength' ? (
          <span className='help-block'>Report name cannot exceed 50 characters</span>
        ) : null}
      </div>
      <div className='form-group col-sm-6'>
        <label htmlFor='timezone' className='control-label'>
          Timezone
        </label>
        <select {...register('timezone')} className='form-control' id='timezone'>
          <option value='US/Pacific'>US/Pacific</option>
          <option value='US/Mountain'>US/Mountain</option>
          <option value='US/Central'>US/Central</option>
          <option value='US/Eastern'>US/Eastern</option>
        </select>
      </div>
      <div className={`form-group col-sm-6 ${errors.timeframe ? 'has-error' : ''}`}>
        <label htmlFor='timeframe' className='control-label'>
          Report Timeframe
        </label>
        <select {...register('timeframe')} className='form-control' id='timeframe'>
          <option value='DAILY'>Daily</option>
          <option value='PRIOR_WEEK'>Prior Week (Delivered on Mondays)</option>
          <option value='MONTH_TO_DATE'>Month To Date</option>
          <option value='PRIOR_MONTH'>Prior Month (Sent on the 1st of each month)</option>
        </select>
      </div>
      <div className={`form-group col-sm-6 ${errors.recipients ? 'has-error' : ''}`}>
        <label className='control-label'>Recipients</label>
        {fields.map((field, index) => {
          return (
            <React.Fragment key={field.id}>
              <div
                key={field.id}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}
              >
                <input
                  type='email'
                  placeholder='name@example.com'
                  className='form-control'
                  style={{ width: '90%' }}
                  {...register(`recipients.${index}.email`, {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                      message: 'Invalid email address',
                    },
                  })}
                />
                <div style={{ display: 'flex', flex: '1', gap: 10, justifyContent: 'center' }}>
                  <a onClick={() => removeRecipient(index)}>
                    <span className='fa fa-minus'></span>
                  </a>
                  <a style={{ marginLeft: 5 }} onClick={() => addRecipient()}>
                    <span className='fa fa-plus'></span>
                  </a>
                </div>
              </div>
              {errors.recipients?.[index]?.email && (
                <span className='help-block'>{errors.recipients[index].email.message}</span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {availableFilters.map((filterType) => {
        if (filterType === 'ds_appt_set') {
          return (
            <div key={filterType} className='checkbox col-xs-12'>
              <label style={{ fontWeight: 600 }}>
                <input type='checkbox' {...register('ds_appt_set')} />{' '}
                {FILTER_CONFIG[filterType].label}
              </label>
            </div>
          );
        } else if (filterType === 'ds_alert_sent') {
          return (
            <div key={filterType} className='checkbox col-xs-12'>
              <label style={{ fontWeight: 600 }}>
                <input type='checkbox' {...register('ds_alert_sent')} />{' '}
                {FILTER_CONFIG[filterType].label}
              </label>
            </div>
          );
        } else {
          return (
            <div className='form-group col-sm-6' key={filterType}>
              <label htmlFor={filterType}>{FILTER_CONFIG[filterType].label}</label>
              {filterType === 'tag_ids' ? (
                <Controller
                  control={control}
                  name='tag_ids'
                  render={({ field }) => (
                    <TagSelect
                      value={field.value}
                      onChange={field.onChange}
                      groupIds={domains.group_ids}
                      locationIds={domains.location_ids}
                    />
                  )}
                />
              ) : filterType === 'employees' ? (
                <Controller
                  control={control}
                  name='employees'
                  render={({ field }) => (
                    <EmployeeSelect
                      value={field.value}
                      onChange={field.onChange}
                      groupIds={domains.group_ids}
                      locationIds={domains.location_ids}
                    />
                  )}
                />
              ) : filterType === 'medium_type' ? (
                <Controller
                  control={control}
                  name='medium_type'
                  render={({ field }) => (
                    <MediumSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              ) : filterType === 'dial_status' ? (
                <Controller
                  control={control}
                  name='dial_status'
                  render={({ field }) => (
                    <DialStatusSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              ) : filterType === 'ds_call_status' ? (
                <Controller
                  control={control}
                  name='ds_call_status'
                  render={({ field }) => (
                    <CallStatusSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              ) : filterType === 'ds_department' ? (
                <Controller
                  control={control}
                  name='ds_department'
                  render={({ field }) => (
                    <DepartmentSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              ) : filterType === 'ls_call_type' ? (
                <Controller
                  control={control}
                  name='ls_call_type'
                  render={({ field }) => (
                    <CallTypeSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              ) : filterType === 'duration' ? (
                <Controller
                  control={control}
                  name='duration'
                  render={({ field }) => (
                    <DurationRange values={field.value} onChange={field.onChange} />
                  )}
                />
              ) : null}
            </div>
          );
        }
      })}
      <div className='row'>
        <button
          onClick={handleSubmit(saveChanges)}
          className='btn btn-primary pull-right detail-row-button'
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className='btn btn-outline-secondary pull-right detail-row-button'
        >
          Cancel
        </button>
      </div>
    </>
  );
}

/**
 * @param {{
 *   initialValue: Domains
 *   showInactive: boolean;
 *   onChange: (value: Domains) => void;
 * }} props
 */
function DomainSelect({ initialValue, showInactive, onChange }) {
  const [availableDomains, setAvailableDomains] = React.useState([]);
  const [error, setError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);
    fetch('/reports/users_groups_and_locations', {
      headers: {
        // @ts-expect-error
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        locations: initialValue.location_ids,
        groups: initialValue.group_ids,
        show_inactive: showInactive ? '1' : '0',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailableDomains(data);
      })
      .catch((error) => {
        console.error(error);
        setError(true);
      })
      .finally(() => setIsLoading(false));
  }, [showInactive]);

  // Need to wrap in useMemo or every time a checkbox is changed,
  // the dropdown rerenders and loses it's internal state.
  const dropdownTreeSelect = React.useMemo(() => {
    return (
      <DropdownTreeSelect
        data={availableDomains}
        texts={{ placeholder: 'Search groups and locations' }}
        onChange={(_currNode, selectedNodes) => {
          const groupIds = [];
          const locationIds = [];
          selectedNodes.forEach((item) => {
            if (item.className?.includes('filter-group')) {
              groupIds.push(item.value);
            } else if (item.className?.includes('filter-location')) {
              locationIds.push(item.value);
            }
          });
          onChange({ group_ids: groupIds, location_ids: locationIds });
        }}
        showDropdown='always'
        showPartiallySelected
        keepTreeOnSearch
        disablePoppingOnBackspace
      />
    );
  }, [availableDomains, onChange]);

  if (error) {
    return <div className='alert alert-danger'>Failed to fetch groups and locations</div>;
  } else if (isLoading) {
    return <p>Loading...</p>;
  } else {
    return dropdownTreeSelect;
  }
}

/**
 * @typedef {{ label: string; value: number; }} Option
 */

/**
 * @param {{
 *   value: number[];
 *   onChange: (tagIds: number[]) => void;
 *   groupIds: number[];
 *   locationIds: number[];
 * }} props
 */
function TagSelect({ value, onChange, groupIds, locationIds }) {
  const [options, setOptions] = React.useState(/** @type {Option[]} */ ([]));
  const [isLoading, setIsLoading] = React.useState(false);

  const selectedOptions = options.filter((option) => value.includes(option.value));

  /**
   * @param {import('react-select').MultiValue<Option>} newValue
   */
  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  React.useEffect(() => {
    if (groupIds.length === 0 && locationIds.length === 0) {
      return;
    }

    setIsLoading(true);
    fetch(`/reports/tags`, {
      headers: {
        // @ts-expect-error
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        group_ids: groupIds,
        location_ids: locationIds,
        show_inactive: '1',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        /** @type {Option[]} */
        const options = data.map((tag) => ({
          value: tag.tag_id,
          label: `${tag.tag_group_name} - ${tag.tag_name}`,
        }));
        setOptions(options);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [groupIds, locationIds]);

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='tag_ids'
      name='tag_ids'
      placeholder='Select Tags'
      isMulti
      isLoading={isLoading}
      options={options}
    />
  );
}

/**
 * @param {{
 *   value: number[];
 *   onChange: (employeeIds: number[]) => void;
 *   groupIds: number[];
 *   locationIds: number[];
 * }} props
 */
function EmployeeSelect({ locationIds, groupIds, onChange, value }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [options, setOptions] = React.useState(/** @type {Option[]} */ ([]));

  const selectedOptions = options.filter((option) => value.includes(option.value));

  /**
   * @param {import('react-select').MultiValue<Option>} newValue
   */
  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  React.useEffect(() => {
    if (locationIds.length === 0 && groupIds.length === 0) {
      return;
    }

    setIsLoading(true);

    fetch(`/reports/representatives`, {
      headers: {
        // @ts-expect-error
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        location_ids: locationIds,
        group_ids: groupIds,
        show_inactive: '1',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        /** @type {Option[]} */
        const formattedOptions = data.map((representative) => ({
          label: `${representative.first_name} ${representative.last_name} (${representative.employee_code})`,
          value: representative.user_id,
        }));
        setOptions(formattedOptions);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [groupIds, locationIds]);

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='employees'
      name='employees'
      placeholder='Select Employees'
      isMulti
      isLoading={isLoading}
      options={options}
    />
  );
}

/**
 * @param {{
 *   value: string[];
 *   onChange: (value: string[]) => void;
 * }} props
 */
function MediumSelect({ value, onChange }) {
  const options = [
    { label: 'Organic', value: 'organic' },
    { label: 'Paid', value: 'paid' },
    { label: 'Referral', value: 'referral' },
    { label: 'Other', value: 'other' },
    { label: 'Direct', value: 'direct' },
  ];

  const selectedOptions = options.filter((option) => value.includes(option.value));

  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='medium_type'
      name='medium_type'
      placeholder='Select Referral'
      isMulti
      options={options}
    />
  );
}

/**
 * @param {{
 *   value: string[];
 *   onChange: (value: string[]) => void;
 * }} props
 */
function DialStatusSelect({ value, onChange }) {
  const options = [
    { label: 'Connected', value: 'ANSWER' },
    { label: 'No Answer', value: 'NOANSWER' },
    { label: 'Abandoned', value: 'ABANDONED' },
    { label: 'Busy', value: 'BUSY' },
    { label: 'Voicemail', value: 'VOICEMAIL' },
  ];

  const selectedOptions = options.filter((option) => value.includes(option.value));

  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='dial_status'
      name='dial_status'
      placeholder='Select Call Result'
      isMulti
      options={options}
    />
  );
}
/**
 * @param {{
 *   value: string[];
 *   onChange: (value: string[]) => void;
 * }} props
 */
function CallStatusSelect({ value, onChange }) {
  const options = [
    { label: 'Connected', value: 'connected' },
    { label: 'Voicemail', value: 'voicemail' },
    { label: 'Hang ups', value: 'hangups' },
    { label: 'Wrong department', value: 'wrongdepartment' },
  ];

  const selectedOptions = options.filter((option) => value.includes(option.value));

  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='ds_call_status'
      name='ds_call_status'
      placeholder='Select Connected Type'
      isMulti
      options={options}
    />
  );
}

/**
 * @param {{
 *   value: string[];
 *   onChange: (value: string[]) => void;
 * }} props
 */
function DepartmentSelect({ value, onChange }) {
  const [options, setOptions] = React.useState(
    /** @type {{ label: string; value: string; }[]} */ ([])
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const selectedOptions = options.filter((option) => value.includes(option.value));

  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  React.useEffect(() => {
    setIsLoading(true);
    fetch(`/reports/users_dealsaver_filters`)
      .then((res) => res.json())
      .then((data) => {
        const options = data.departments.map((department) => ({
          value: department,
          label: department,
        }));
        setOptions(options);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='ds_department'
      name='ds_department'
      placeholder='Select Departments'
      isMulti
      isLoading={isLoading}
      options={options}
    />
  );
}

/**
 * @param {{
 *   value: string[];
 *   onChange: (value: string[]) => void;
 * }} props
 */
function CallTypeSelect({ value, onChange }) {
  const [options, setOptions] = React.useState(
    /** @type {{label: string; options: {label: string; value: string;}[];}[]} */ ([])
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const flatOptions = options.flatMap((group) => group.options);
  const selectedOptions = flatOptions.filter((option) => value.includes(option.value));

  function handleChange(newValue) {
    onChange(newValue.map((option) => option.value));
  }

  React.useEffect(() => {
    setIsLoading(true);
    fetch(`/reports/users_dealsaver_filters`)
      .then((res) => res.json())
      .then(({ call_types }) => {
        const prospectOptions = call_types
          .filter((callType) => callType.is_prospect === 1)
          .map((callType) => ({ label: callType.text, value: callType.text }));

        const nonProspectOptions = call_types
          .filter((callType) => callType.is_prospect === 0)
          .map((callType) => ({ label: callType.text, value: callType.text }));

        const options = [
          { label: 'Prospects', options: prospectOptions },
          { label: 'Non-Prospects', options: nonProspectOptions },
        ];

        setOptions(options);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      id='ls_call_type'
      name='ls_call_type'
      placeholder='Select Call Types'
      isMulti
      isLoading={isLoading}
      options={options}
    />
  );
}
